'use client'

/**
 * The language selector — ONE implementation shared by the marketing nav and the logged-in chrome
 * (WealthOSTopBar), so the control sits in the same place before and after sign-in. Selecting a language
 * routes through MyCIOLanguageContext.setLanguage, which persists it: cookie always, and the user's stored
 * default via PUT /api/mycio/preferences/language when signed in — so it follows them across devices.
 * Extracted verbatim from the landing Nav (trigger + menu + the responsive flag/code swap) — a pixel-identical
 * no-op there.
 */

import { useEffect, useRef, useState } from 'react'
import { MC } from '@/lib/mycio/theme'
import { useMyCIOLanguage } from '@/lib/mycio/MyCIOLanguageContext'
import { MYCIO_LANGUAGES, getMyCIOLanguage, type MyCIOLanguageCode } from '@/lib/mycio/language'
import { getMyCIOCopy } from '@/lib/mycio/translations'

export default function LanguageSelect({ menuTop = 44 }: { menuTop?: number }) {
  const [langOpen, setLangOpen] = useState(false)
  const { language, setLanguage } = useMyCIOLanguage()
  const selectedLanguage = getMyCIOLanguage(language)
  const copy = getMyCIOCopy(language)
  const wrapRef = useRef<HTMLDivElement>(null)
  // click-away close (the landing nav never needed it because the page scrolled; in the app chrome the
  // menu would otherwise sit open over the plan)
  useEffect(() => {
    if (!langOpen) return
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setLangOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [langOpen])

  return (
    <div ref={wrapRef} className="mc-lang-keep" style={{ position: 'relative' }}>
      <style>{`
        .mc-lang-code { display: none }
        @media (max-width:640px){
          /* Show the human language NAME on phones (e.g. "English", "日本語") — not a bare 2-letter code.
             Keep it compact so it still fits the tight action row. */
          .mc-lang-label { max-width: 86px !important }
          .mc-lang-menu { position: fixed !important; top: 66px !important; left: 14px !important; right: 14px !important; width: auto !important }
        }
      `}</style>
      <button
        type="button"
        onClick={() => setLangOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={langOpen}
        className="mc-langbtn"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          fontSize: 14,
          color: MC.muted,
          background: 'transparent',
          border: `1px solid ${MC.border}`,
          borderRadius: 999,
          padding: '6px 11px',
          cursor: 'pointer',
        }}
      >
        <span style={{ width: 18, height: 13, borderRadius: 2, background: selectedLanguage.flagColor, display: 'inline-block' }} />
        <span className="mc-lang-label" style={{ maxWidth: 92, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedLanguage.nativeLabel}</span>
        <span className="mc-lang-code" style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.03em', color: MC.ink }}>{selectedLanguage.code.toUpperCase()}</span>
        <span style={{ fontSize: 10, color: MC.faint2 }}>▾</span>
      </button>
      {langOpen && (
        <div
          className="mc-lang-menu"
          style={{
            position: 'absolute',
            top: menuTop,
            right: 0,
            width: 210,
            background: MC.surface,
            border: `1px solid ${MC.border}`,
            borderRadius: 12,
            boxShadow: '0 18px 40px -20px rgba(35,33,28,0.4)',
            padding: 6,
            zIndex: 90,
          }}
        >
          <div style={{ maxHeight: 278, overflowY: 'auto' }}>
            {MYCIO_LANGUAGES.map((option) => {
              const isSelected = option.code === selectedLanguage.code
              return (
                <button
                  key={option.code}
                  type="button"
                  onClick={() => {
                    void setLanguage(option.code as MyCIOLanguageCode)
                    setLangOpen(false)
                  }}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, background: isSelected ? MC.sageBg : 'transparent', border: 'none', borderRadius: 8, padding: '8px 10px', cursor: 'pointer', textAlign: 'left' }}
                >
                  <span style={{ width: 20, height: 14, borderRadius: 2, background: option.flagColor, display: 'inline-block', flex: '0 0 auto' }} />
                  <span style={{ fontSize: 14, color: MC.ink, whiteSpace: 'nowrap' }}>{option.nativeLabel}</span>
                </button>
              )
            })}
          </div>
          <p style={{ fontSize: 11, color: MC.faint2, margin: '6px 8px 4px', lineHeight: 1.45 }}>
            {copy.nav.saved}
          </p>
        </div>
      )}
    </div>
  )
}
